import { computed, ref, watch } from "vue";
import {
  DEFAULT_REASONING_EFFORT,
  REASONING_EFFORT_OPTIONS,
  type ChatAttachment,
  type ReasoningEffort,
} from "@/services/ai_agent";
import { useSidepanel } from "@/composables/useSidepanel";
import {
  revokeAttachmentPreviews,
  type PendingAttachment,
} from "@/composables/usePendingAttachments";

const STORAGE_KEY = "aiChatSession";
const EFFORT_STORAGE_KEY = "aiChatEffort";

function loadPersistedSessionId(): number | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed.sessionId === "number") {
      return parsed.sessionId;
    }
  } catch {
    // Corrupted data — ignore
  }
  return null;
}

function persistSession(sessionId: number | null): void {
  if (sessionId) {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ sessionId }));
  } else {
    sessionStorage.removeItem(STORAGE_KEY);
  }
}

function restoreEffort(): ReasoningEffort {
  try {
    const stored = sessionStorage.getItem(EFFORT_STORAGE_KEY);
    if (
      stored &&
      REASONING_EFFORT_OPTIONS.some((opt) => opt.value === stored)
    ) {
      return stored as ReasoningEffort;
    }
  } catch {
    // Private mode / blocked storage: fall through to the default.
  }
  return DEFAULT_REASONING_EFFORT;
}

function persistEffort(value: ReasoningEffort): void {
  try {
    sessionStorage.setItem(EFFORT_STORAGE_KEY, value);
  } catch {
    // Nothing to do — the in-memory ref is still the source of truth.
  }
}

// AI chat's own state — the sidepanel knows nothing about sessions; "ai" is
// a mode like any other, and this is that mode's brain.
const phase = ref<"prompt" | "chat">("prompt");
const sessionId = ref<number | null>(loadPersistedSessionId());
const pendingPrompt = ref<string | null>(null);
const pendingInitialMessage = ref<string | null>(null);
const pendingInitialAttachments = ref<ChatAttachment[] | undefined>();
// Live here, not in the input components, so unsent text and images survive
// the sidepanel swapping to another mode and back.
const draft = ref("");
const draftAttachments = ref<PendingAttachment[]>([]);
// Bumped whenever the draft is discarded, so a submit still in flight can tell
// its draft apart from one written for another chat, even with the same text.
const draftRevision = ref(0);
const submittingRevision = ref<number | null>(null);
// Shared, not per panel: a panel remounted mid-upload would otherwise offer
// to send the same draft a second time.
const isDraftSubmitting = computed(
  () => submittingRevision.value === draftRevision.value,
);

// Failed sends the server never saved, kept by chat when that chat wasn't on
// screen with an empty input to take them back: Send cleared the only copy.
const unsentDrafts = new Map<
  number,
  { text: string; attachments: PendingAttachment[] }
>();
// Chats a page keeps unsent messages for, oldest dropped first: a user who
// never reopens them would otherwise hold every one until a reload.
const UNSENT_DRAFTS_MAX = 20;

function offerUnsentDraft(id: number): void {
  const unsent = unsentDrafts.get(id);
  if (!unsent || draft.value || draftAttachments.value.length > 0) return;
  unsentDrafts.delete(id);
  draft.value = unsent.text;
  draftAttachments.value = unsent.attachments;
}

export function returnUnsentDraft(
  id: number,
  text: string,
  attachments: PendingAttachment[],
): void {
  const earlier = unsentDrafts.get(id);
  unsentDrafts.delete(id);
  unsentDrafts.set(id, {
    text: [earlier?.text, text].filter(Boolean).join("\n\n"),
    attachments: [...(earlier?.attachments ?? []), ...attachments],
  });
  if (unsentDrafts.size > UNSENT_DRAFTS_MAX) {
    const [oldest] = unsentDrafts.keys();
    unsentDrafts.delete(oldest);
  }
  if (sessionId.value === id) offerUnsentDraft(id);
}

function clearDraft(): void {
  draft.value = "";
  revokeAttachmentPreviews(draftAttachments.value);
  draftAttachments.value = [];
  draftRevision.value += 1;
}

export interface DraftSubmission {
  isCurrent: () => boolean;
  end: () => void;
}

function startDraftSubmit(): DraftSubmission {
  const revision = draftRevision.value;
  submittingRevision.value = revision;
  return {
    isCurrent: () => draftRevision.value === revision,
    end: () => {
      if (submittingRevision.value === revision) {
        submittingRevision.value = null;
      }
    },
  };
}
// Shared by AIPromptPanel and AIChatPanel so the level picked before the first
// message carries into the running chat. Persisted like sessionId: a reload
// that restores the conversation but resets this would show a setting the
// next message would not use.
const chatEffort = ref<ReasoningEffort>(restoreEffort());

watch(sessionId, (val) => persistSession(val));
// Kept until restoring it won't overwrite a newer draft.
watch(
  () => [sessionId.value, draft.value, draftAttachments.value.length] as const,
  ([id]) => {
    if (id !== null) offerUnsentDraft(id);
  },
);
watch(chatEffort, (val) => persistEffort(val));

/** The ai mode def's onOpen hook: resume the chat if a session exists. */
export function syncAiPhaseForOpen(): void {
  phase.value = sessionId.value ? "chat" : "prompt";
}

// The created session is the authority on its own effort vocabulary, and it
// is the first thing in this flow that knows the model for certain. Every
// other source is a prediction that can be wrong: a config answer cached for
// another organization, one that outlived an admin changing the default
// model, one that never arrived, or one whose fetch failed and left the panel
// showing the full set. Each of those puts an unsupported level into
// `chatEffort`, and `AIChatPanel` posts the initial message with it before
// its selector has mounted to correct the shared value — so the backend
// clamps the first request and the slider then changes shape underneath the
// choice, which is the mismatch this whole change exists to remove.
//
// An empty or absent list means the model takes no effort setting at all;
// the value is ignored on the wire, so the stored preference is left alone
// for the next session that can use it.
export function reconcileEffortWithSession(
  supported: readonly ReasoningEffort[] | undefined,
): void {
  if (!supported?.length) return;
  if (supported.includes(chatEffort.value)) return;
  // Product order, matching what `ReasoningEffortSelect` would have settled
  // on, so the correction is the same whichever one gets there first.
  const corrected = REASONING_EFFORT_OPTIONS.find((opt) =>
    supported.includes(opt.value),
  );
  if (corrected) chatEffort.value = corrected.value;
}

export function resetAiChatSession(): void {
  phase.value = "prompt";
  sessionId.value = null;
  pendingPrompt.value = null;
  pendingInitialMessage.value = null;
  pendingInitialAttachments.value = undefined;
  clearDraft();
  unsentDrafts.clear();
  chatEffort.value = DEFAULT_REASONING_EFFORT;
  persistEffort(DEFAULT_REASONING_EFFORT);
}

export function useAiChatSession() {
  const sidepanel = useSidepanel();

  function handleSessionCreated(
    id: number,
    prompt?: string,
    attachments?: ChatAttachment[],
  ): void {
    pendingInitialMessage.value = prompt ?? null;
    pendingInitialAttachments.value = attachments;
    sessionId.value = id;
    phase.value = "chat";
  }

  function resumeSession(id: number): void {
    // A draft written for one chat makes no sense in another.
    if (id !== sessionId.value) clearDraft();
    handleSessionCreated(id);
    offerUnsentDraft(id);
  }

  function restart(): void {
    clearDraft();
    sessionId.value = null;
    phase.value = "prompt";
  }

  function openWithPrompt(prompt: string): void {
    pendingPrompt.value = prompt;
    restart();
    sidepanel.openMode("ai");
    // openMode's onOpen derives phase from sessionId; restart just nulled it,
    // so the panel lands on the prompt phase as intended.
  }

  return {
    phase,
    sessionId,
    pendingPrompt,
    pendingInitialMessage,
    pendingInitialAttachments,
    chatEffort,
    draft,
    draftAttachments,
    isDraftSubmitting,
    startDraftSubmit,
    handleSessionCreated,
    resumeSession,
    restart,
    openWithPrompt,
  };
}
